export type SeasonType =
    | 'christmas'
    | 'newYear'
    | 'winter'
    | 'spring'
    | 'easter'
    | 'summer'
    | 'halloween'
    | 'autumn';

export interface ParticleConfig {
    symbol: string;
    count: number;
    colors?: string[];
    rotationSpeed?: 'slow' | 'medium' | 'fast';
}

export interface LightsConfig {
    colors: string[];
}

export interface FlyingConfig {
    emoji: string;
    frequency: number; // seconds between appearances
    speed: number; // seconds for animation
}

export interface SeasonConfig {
    name: string;
    particles: ParticleConfig;
    lights: LightsConfig;
    flying: FlyingConfig | null;
}

export const seasons: Record<SeasonType, SeasonConfig> = {
    christmas: {
        name: 'Noël',
        particles: { symbol: '❄', count: 50, rotationSpeed: 'medium' },
        lights: { colors: ['#ff0000', '#00ff00', '#ffcc00', '#ff0000', '#00ff00'] },
        flying: { emoji: '🦌🎅🎁', frequency: 45, speed: 8 },
    },
    newYear: {
        name: 'Nouvel An',
        particles: { symbol: '✨', count: 60, colors: ['#ffd700', '#c0c0c0', '#ff69b4', '#00ffff'], rotationSpeed: 'fast' },
        lights: { colors: ['#ffd700', '#c0c0c0', '#ffd700', '#c0c0c0', '#ffd700'] },
        flying: { emoji: '🎆', frequency: 30, speed: 6 },
    },
    winter: {
        name: 'Hiver',
        particles: { symbol: '❄', count: 30, rotationSpeed: 'slow' },
        lights: { colors: ['#87ceeb', '#ffffff', '#b0e0e6', '#87ceeb', '#ffffff'] },
        flying: null,
    },
    spring: {
        name: 'Printemps',
        particles: { symbol: '🌸', count: 35, colors: ['#ffb7c5', '#ffc0cb', '#ff69b4'], rotationSpeed: 'slow' },
        lights: { colors: ['#ffb7c5', '#98fb98', '#ffb7c5', '#98fb98', '#ffb7c5'] },
        flying: { emoji: '🦋', frequency: 40, speed: 10 },
    },
    easter: {
        name: 'Pâques',
        particles: { symbol: '✨', count: 40, colors: ['#ffb6c1', '#98fb98', '#87ceeb', '#fffacd', '#dda0dd'], rotationSpeed: 'medium' },
        lights: { colors: ['#ffb6c1', '#98fb98', '#87ceeb', '#fffacd', '#dda0dd'] },
        flying: { emoji: '🐰🥚', frequency: 50, speed: 9 },
    },
    summer: {
        name: 'Été',
        particles: { symbol: '☀', count: 20, colors: ['#ffd700', '#ff8c00', '#ffff00'], rotationSpeed: 'slow' },
        lights: { colors: ['#ffd700', '#ff8c00', '#ffd700', '#ff8c00', '#ffd700'] },
        flying: { emoji: '✈️', frequency: 60, speed: 7 },
    },
    halloween: {
        name: 'Halloween',
        particles: { symbol: '🦇', count: 25, rotationSpeed: 'fast' },
        lights: { colors: ['#ff6600', '#9932cc', '#ff6600', '#9932cc', '#ff6600'] },
        flying: { emoji: '🧹🧙', frequency: 45, speed: 8 },
    },
    autumn: {
        name: 'Automne',
        particles: { symbol: '🍂', count: 30, colors: ['#d2691e', '#ff8c00', '#8b4513', '#cd853f'], rotationSpeed: 'slow' },
        lights: { colors: ['#ff6600', '#daa520', '#8b0000', '#ff8c00', '#cd853f'] },
        flying: { emoji: '🦅', frequency: 55, speed: 9 },
    },
};

// Easter date calculation (Computus algorithm)
function getEasterDate(year: number): Date {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
}

export function getCurrentSeason(date: Date = new Date()): SeasonType {
    const month = date.getMonth(); // 0-11
    const day = date.getDate();
    const year = date.getFullYear();

    // Helper to check if date is in range (inclusive)
    const isInRange = (startMonth: number, startDay: number, endMonth: number, endDay: number): boolean => {
        const checkDate = month * 100 + day;
        const start = startMonth * 100 + startDay;
        const end = endMonth * 100 + endDay;

        if (start <= end) {
            return checkDate >= start && checkDate <= end;
        } else {
            // Range crosses year boundary
            return checkDate >= start || checkDate <= end;
        }
    };

    // Priority 1: New Year (Dec 31 - Jan 2)
    if (isInRange(11, 31, 0, 2)) {
        return 'newYear';
    }

    // Priority 2: Christmas (Dec 15 - Jan 6, excluding New Year)
    if (isInRange(11, 15, 0, 6)) {
        return 'christmas';
    }

    // Priority 3: Easter (~2 weeks around Easter Sunday)
    const easter = getEasterDate(year);
    const easterStart = new Date(easter);
    easterStart.setDate(easter.getDate() - 7);
    const easterEnd = new Date(easter);
    easterEnd.setDate(easter.getDate() + 7);

    if (date >= easterStart && date <= easterEnd) {
        return 'easter';
    }

    // Priority 4: Halloween (Oct 15 - Nov 1)
    if (isInRange(9, 15, 10, 1)) {
        return 'halloween';
    }

    // Default seasons by date
    // Winter: Jan 7 - Mar 19
    if (isInRange(0, 7, 2, 19)) {
        return 'winter';
    }

    // Spring: Mar 20 - Jun 20
    if (isInRange(2, 20, 5, 20)) {
        return 'spring';
    }

    // Summer: Jun 21 - Sep 22
    if (isInRange(5, 21, 8, 22)) {
        return 'summer';
    }

    // Autumn: Sep 23 - Dec 14
    if (isInRange(8, 23, 11, 14)) {
        return 'autumn';
    }

    // Fallback (should not happen)
    return 'winter';
}

export function getSeasonConfig(season: SeasonType = getCurrentSeason()): SeasonConfig {
    return seasons[season];
}
